import { Controller } from "@hotwired/stimulus"

/**
 * Countries browse controller - search + continent filtering for /countries page
 *
 * Usage:
 *   data-controller="countries-browse" on the main container
 *   data-countries-browse-target="search" on the search input
 *   data-countries-browse-target="continentBtn" on continent filter buttons
 *   data-countries-browse-target="card" on each country card
 *   data-countries-browse-target="grid" on the grid container
 *   data-countries-browse-target="empty" on empty state element
 *   data-countries-browse-target="count" on the count display
 *   data-countries-browse-target="clearBtn" on the clear search button
 *
 *   data-name="country name" on cards (lowercased for search)
 *   data-continent="Africa" on cards and buttons
 */
export default class extends Controller<HTMLElement> {
  static targets = ["search", "continentBtn", "card", "grid", "empty", "count", "clearBtn"]

  declare readonly searchTarget: HTMLInputElement
  declare readonly continentBtnTargets: HTMLButtonElement[]
  declare readonly cardTargets: HTMLElement[]
  declare readonly gridTarget: HTMLElement
  declare readonly emptyTarget: HTMLElement
  declare readonly countTarget: HTMLElement
  declare readonly clearBtnTarget: HTMLElement
  declare readonly hasEmptyTarget: boolean
  declare readonly hasCountTarget: boolean
  declare readonly hasClearBtnTarget: boolean

  private activeContinent = "all"
  private searchQuery = ""
  private debounceTimer: ReturnType<typeof setTimeout> | null = null

  connect() {
    // Restore state from URL params
    const params = new URLSearchParams(window.location.search)
    const continent = params.get("continent")
    const search = params.get("search")

    if (continent) {
      this.activeContinent = continent
    }
    if (search) {
      this.searchQuery = search.toLowerCase()
      this.searchTarget.value = search
    }

    this.applyFilters(false)
  }

  // Called on input event from search field
  search() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)

    this.debounceTimer = setTimeout(() => {
      this.searchQuery = this.searchTarget.value.trim().toLowerCase()
      this.applyFilters(true)
      this.updateUrl()

      // Show/hide clear button
      if (this.hasClearBtnTarget) {
        this.clearBtnTarget.classList.toggle("hidden", this.searchQuery.length === 0)
      }
    }, 100)
  }

  // Called by clear search button
  clearSearch() {
    this.searchTarget.value = ""
    this.searchQuery = ""
    this.applyFilters(true)
    this.updateUrl()
    this.searchTarget.focus()

    if (this.hasClearBtnTarget) {
      this.clearBtnTarget.classList.add("hidden")
    }
  }

  // Called by continent filter buttons
  filterContinent(event: Event) {
    const button = event.currentTarget as HTMLButtonElement
    this.activeContinent = button.dataset.continent || "all"
    this.applyFilters(true)
    this.updateUrl()
  }

  private applyFilters(animate: boolean) {
    // Update continent button styles
    this.continentBtnTargets.forEach(btn => {
      const btnContinent = btn.dataset.continent || "all"
      const isActive = btnContinent === this.activeContinent

      if (isActive) {
        btn.classList.remove("btn-ghost", "border", "border-base-300")
        btn.classList.add("btn-primary")
      } else {
        btn.classList.remove("btn-primary")
        btn.classList.add("btn-ghost", "border", "border-base-300")
      }
    })

    // Filter cards
    let visibleCount = 0
    const totalCount = this.cardTargets.length

    this.cardTargets.forEach(card => {
      const name = (card.dataset.name || "").toLowerCase()
      const continent = card.dataset.continent || ""

      const matchesSearch = this.searchQuery === "" || name.includes(this.searchQuery)
      const matchesContinent = this.activeContinent === "all" || continent === this.activeContinent

      const show = matchesSearch && matchesContinent

      if (show) {
        visibleCount++
        card.classList.remove("hidden")

        if (animate) {
          card.style.opacity = "0"
          card.style.transform = "translateY(8px)"
          requestAnimationFrame(() => {
            card.style.transition = "opacity 150ms ease, transform 150ms ease"
            card.style.opacity = "1"
            card.style.transform = "translateY(0)"
          })
        }
      } else {
        card.classList.add("hidden")
      }
    })

    // Update count display
    if (this.hasCountTarget) {
      if (visibleCount === totalCount) {
        this.countTarget.textContent = `${totalCount} countries`
      } else {
        this.countTarget.textContent = `${visibleCount} of ${totalCount} countries`
      }
    }

    // Show/hide empty state
    if (this.hasEmptyTarget) {
      if (visibleCount === 0) {
        this.emptyTarget.classList.remove("hidden")
        this.gridTarget.classList.add("hidden")
      } else {
        this.emptyTarget.classList.add("hidden")
        this.gridTarget.classList.remove("hidden")
      }
    }
  }

  private updateUrl() {
    const url = new URL(window.location.href)

    if (this.activeContinent === "all") {
      url.searchParams.delete("continent")
    } else {
      url.searchParams.set("continent", this.activeContinent)
    }

    if (this.searchQuery === "") {
      url.searchParams.delete("search")
    } else {
      url.searchParams.set("search", this.searchQuery)
    }

    history.replaceState({}, "", url)
  }
}
